/**
 * The page the PDF renderer prints (/print/<code>). Every page of the
 * dashboard or report is drawn one after another at its true size (a 16:9
 * slide, an A4 page, or a dashboard tab), with all its numbers loaded first.
 * When everything is on screen and fonts are in, it sets
 * window.__printReady; a failure sets window.__printError instead.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';

import { AudienceContext } from '../reports/audience.ts';
import { ChangeFormatContext, changeFormatOf } from '../reports/change-format.ts';
import { PAGE_FORMAT } from '../reports/grid.ts';
import { coverFacts } from '../reports/PageArt.tsx';
import { DESIGN_WIDTH, PageView } from '../reports/PageView.tsx';
import type { WidgetResult } from '../reports/reports-api.ts';
import { ThemeScope } from '../reports/ThemeScope.tsx';
import { printApi, type OpenedPrint } from './print-api.ts';

declare global {
  interface Window {
    __printReady?: boolean;
    __printError?: string;
  }
}

const DASHBOARD_WIDTH = 1100;
const NO_OP = () => undefined;

interface Loaded {
  opened: OpenedPrint;
  pages: { results: Record<string, WidgetResult>; range: { from: string; to: string } }[];
}

const DASHBOARD_MARGIN = 24;

/**
 * Page sizes. A report keeps its own page shape (16:9 slide, A4 page). A
 * dashboard has no fixed height, so each tab prints as ONE page exactly as
 * tall as its content (named @page rules, measured once drawn).
 */
function pageCss(kind: string, heights: number[] | null): string {
  const format = PAGE_FORMAT[kind];
  const width = DESIGN_WIDTH[kind] ?? DASHBOARD_WIDTH;
  const common = `html, body { background: var(--sheet); }
    .pr-page { break-after: page; }
    .pr-page:last-child { break-after: auto; }`;
  if (format)
    return `@page { size: ${width}px ${Math.round(width * format.ratio)}px; margin: 0; }\n${common}`;
  const pages = (heights ?? [])
    .map(
      (height, index) =>
        `@page tab${index} { size: ${width + 2 * DASHBOARD_MARGIN}px ${Math.ceil(height) + 2 * DASHBOARD_MARGIN}px; margin: ${DASHBOARD_MARGIN}px; }
         .pr-page-${index} { page: tab${index}; }`,
    )
    .join('\n');
  return `${pages}\n${common}`;
}

export function PrintReport({ token }: { token: string }): ReactNode {
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [heights, setHeights] = useState<number[] | null>(null);
  const main = useRef<HTMLElement | null>(null);

  useEffect(() => {
    (async () => {
      const opened = await printApi.open(token);
      const pages = await Promise.all(
        opened.pages.map(async (_, index) => {
          const data = await printApi.query(token, index);
          return {
            results: Object.fromEntries(data.results.map((result) => [result.id, result])),
            range: data.range,
          };
        }),
      );
      setLoaded({ opened, pages });
    })().catch((error: unknown) => {
      window.__printError =
        error instanceof Error ? error.message : 'The report could not be loaded.';
      window.__printReady = true;
    });
  }, [token]);

  // Two frames for layout and the web fonts, then measure (dashboards) and
  // mark ready, so nothing prints half-drawn or spills onto an extra page.
  useEffect(() => {
    if (!loaded) return;
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        void document.fonts.ready.then(() => {
          const sections = Array.from(main.current?.querySelectorAll('.pr-page') ?? []);
          setHeights(sections.map((section) => section.getBoundingClientRect().height));
        });
      }),
    );
  }, [loaded]);
  useEffect(() => {
    if (heights) requestAnimationFrame(() => (window.__printReady = true));
  }, [heights]);

  if (!loaded) return <p className="pt-empty">Preparing the PDF…</p>;
  const { opened } = loaded;
  const { document: report } = opened;
  const width = DESIGN_WIDTH[report.kind] ?? DASHBOARD_WIDTH;
  return (
    <AudienceContext.Provider value="client">
      <ChangeFormatContext.Provider value={changeFormatOf(report.settings.compare)}>
        <style>{pageCss(report.kind, heights)}</style>
        <main className="pr" ref={main}>
          {opened.pages.map((page, index) => {
            const data = loaded.pages[index];
            const keyOf = (at: number) => page.widgets[at]?.id ?? `w${at}`;
            return (
              <section
                key={page.id ?? index}
                className={`pr-page pr-page-${index}`}
                style={{ width }}
              >
                <ThemeScope
                  theme={report.theme}
                  layout={PAGE_FORMAT[report.kind] ? 'report' : 'dashboard'}
                >
                  <PageView
                    kind={report.kind}
                    page={page}
                    pages={opened.pages}
                    fixedWidth={PAGE_FORMAT[report.kind] ? width : undefined}
                    cover={coverFacts({
                      reportName: report.name,
                      clientName: opened.client.name,
                      period: data?.range ?? null,
                      preset: report.settings.range?.preset ?? 'LAST_MONTH',
                      branding: opened.branding,
                    })}
                    canvas={{
                      widgets: page.widgets,
                      results: data?.results ?? {},
                      sources: opened.catalogue.sources,
                      editable: false,
                      autoAlign: false,
                      selected: null,
                      onSelect: NO_OP,
                      onChange: NO_OP,
                      onRemove: NO_OP,
                      keyOf,
                    }}
                  />
                </ThemeScope>
              </section>
            );
          })}
        </main>
      </ChangeFormatContext.Provider>
    </AudienceContext.Provider>
  );
}
